import { type ReactElement, useMemo } from 'react'
import { cn, PAGE_HEIGHT } from '@util'
import { type RoleData } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { Group, type MantineBreakpoint, Stack, TextInput } from '@mantine/core'
import { useCloseNavbar, useContainerBreakpoint } from '@hooks'
import { useRoleListState, useTranslation } from '@front/model'
import { ButtonRefresh, NestedNavigation, NotReady, type PageRoute } from '@components'
import { RoleSelectNavigation } from './RoleSelectNavigation'

const MANAGE_SECTION = 'Manage'
const ROLES_SECTION = 'Roles'
const SIDEBAR_BREAKPOINT: MantineBreakpoint = 'sm'

function RoleLayout(): ReactElement {
  useCloseNavbar()
  const { t } = useTranslation()
  const { data, filtered, filter, setFilter, isLoading, isFetching, error, refetch } = useRoleListState()
  const { ref, isAtLeast: showSidebar } = useContainerBreakpoint(SIDEBAR_BREAKPOINT)

  const routes = useMemo<PageRoute[]>(
    () => [
      {
        path: '/settings/role/add',
        label: t('Add Role'),
        icon: 'add',
        section: MANAGE_SECTION,
      },
      {
        path: '/settings/role/trash',
        label: t('View Trash'),
        icon: 'archive',
        section: MANAGE_SECTION,
      },
      ...filtered.map(toRoleRoute),
    ],
    [filtered, t]
  )

  return (
    <div ref={ref} className={cn(PAGE_HEIGHT, 'flex w-full')}>
      {showSidebar ? (
        <NestedNavigation
          loading={isLoading}
          routes={routes}
          sectionLabel={ROLES_SECTION}
          header={
            <Stack gap='sm'>
              <Group gap='xs' wrap='nowrap'>
                <TextInput
                  flex={1}
                  placeholder={t('Filter...')}
                  value={filter}
                  onChange={(e) => setFilter(e.currentTarget.value)}
                />
                <ButtonRefresh isFetching={isFetching} refetch={refetch} />
              </Group>
              {error && <NotReady inline error={error} />}
            </Stack>
          }
        />
      ) : (
        <RoleSelectNavigation error={error} isLoading={isLoading} roles={data ?? []} />
      )}
    </div>
  )
}

const toRoleRoute = (role: RoleData): PageRoute => ({
  path: `/settings/role/${role.roleId}`,
  label: role.name,
  section: ROLES_SECTION,
  search: true,
})

export const Route = createFileRoute('/settings/role')({
  component: RoleLayout,
})
